import { useState, useEffect, useRef, useCallback } from 'react';
import { Section } from '../components/Section';
import { Container } from '../components/Container';

interface ScreenshotItem {
  src: string;
  label: string;
}

const screenshots: ScreenshotItem[] = [
  { src: '/images/admin-panel.png', label: 'Painel do lojista' },
  { src: '/images/admin-charts.png', label: 'Gráficos de receita' },
  { src: '/images/admin-mixed.png', label: 'Visão completa' },
  { src: '/images/store-front-widget.png', label: 'Widget na vitrine' },
];

function DeviceFrame({ src, label }: ScreenshotItem) {
  return (
    <div>
      <div
        style={{
          borderRadius: 12,
          backgroundColor: '#f8f9fa',
          border: '1px solid #e5e7eb',
          paddingTop: 24,
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {/* Browser chrome dots */}
        <div
          style={{
            position: 'absolute',
            top: 8,
            left: 12,
            display: 'flex',
            gap: 6,
          }}
        >
          <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#e5e7eb' }} />
          <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#e5e7eb' }} />
          <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#e5e7eb' }} />
        </div>

        <img
          src={src}
          alt={label}
          loading="lazy"
          style={{
            width: '100%',
            display: 'block',
          }}
        />
      </div>
      <p
        style={{
          fontSize: '14px',
          fontWeight: 600,
          color: '#111827',
          textAlign: 'center',
          marginTop: 'var(--spacing-sm)',
        }}
      >
        {label}
      </p>
    </div>
  );
}

export function Screenshots() {
  const [activeIndex, setActiveIndex] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  const setItemRef = useCallback((index: number) => (el: HTMLDivElement | null) => {
    itemRefs.current[index] = el;
  }, []);

  useEffect(() => {
    const items = itemRefs.current.filter(Boolean) as HTMLDivElement[];
    if (items.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const index = items.indexOf(entry.target as HTMLDivElement);
            if (index !== -1) {
              setActiveIndex(index);
            }
          }
        }
      },
      {
        root: carouselRef.current,
        threshold: 0.5,
      }
    );

    for (const item of items) {
      observer.observe(item);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <Section background="white">
      <Container maxWidth="content">
        <h2
          style={{
            fontSize: '24px',
            fontFamily: 'var(--font-heading)',
            fontWeight: 800,
            color: '#111827',
            textAlign: 'center',
            marginBottom: 'var(--spacing-xl)',
            lineHeight: 1.2,
          }}
        >
          Veja o Assine em ação
        </h2>

        {/* Carousel */}
        <div
          ref={carouselRef}
          className="screenshots-carousel"
          style={{
            display: 'flex',
            overflowX: 'auto',
            scrollSnapType: 'x mandatory',
            WebkitOverflowScrolling: 'touch',
            gap: 'var(--spacing-lg)',
            paddingBottom: 'var(--spacing-md)',
            scrollbarWidth: 'none',
          }}
        >
          {screenshots.map((screenshot, index) => (
            <div
              key={screenshot.label}
              ref={setItemRef(index)}
              className="carousel-item"
              style={{
                scrollSnapAlign: 'center',
                flexShrink: 0,
              }}
            >
              <DeviceFrame {...screenshot} />
            </div>
          ))}
        </div>

        {/* Scroll indicator dots */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: 'var(--spacing-sm)',
            marginTop: 'var(--spacing-md)',
          }}
        >
          {screenshots.map((screenshot, index) => (
            <button
              key={screenshot.label}
              type="button"
              aria-label={`Ver ${screenshot.label}`}
              onClick={() => {
                const item = itemRefs.current[index];
                if (item) {
                  item.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
                }
              }}
              style={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: index === activeIndex
                  ? 'var(--brand-gradient)'
                  : '#E5E7EB',
                transition: 'background 200ms ease',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
              }}
            />
          ))}
        </div>
      </Container>
      <style>{`
        .screenshots-carousel::-webkit-scrollbar {
          display: none;
        }
        .carousel-item {
          width: 80%;
        }
        @media (max-width: 639px) {
          .carousel-item {
            width: 90%;
          }
        }
      `}</style>
    </Section>
  );
}
